<template>
  <div v-if="value" class="inv-overlay" @click.self="$emit('input', false)">
    <div class="inv-modal inv-modal--lg">
      <div class="inv-modal__header">
        <span>{{ editMode ? '✎ EDIT' : '+ NEW TOOL' }}</span>
        <button class="inv-modal__close" @click="$emit('input', false)">×</button>
      </div>
      <div class="inv-modal__body">
        <div class="inv-form-grid">
          <div class="inv-field inv-field--wide">
            <label class="inv-label">NAME *</label>
            <input v-model="localForm.name" class="inv-input" :class="{ 'inv-input--error': errors.name }" placeholder="Tool name" />
            <span v-if="errors.name" class="inv-field-error">Required field</span>
          </div>
          <div class="inv-field">
            <label class="inv-label">MODEL</label>
            <input v-model="localForm.model" class="inv-input" placeholder="e.g. XPS 9520" />
          </div>
          <div class="inv-field">
            <label class="inv-label">SERIAL NUMBER</label>
            <input v-model="localForm.serial_number" class="inv-input inv-input--mono" placeholder="SN-XXXX-YYYY" />
          </div>
          <div class="inv-field">
            <label class="inv-label">TYPE</label>
            <select v-model="localForm.type" class="inv-select">
              <option v-for="o in typeOptions.filter(v => v.value !== null)" :key="o.value" :value="o.value">{{ o.text }}</option>
            </select>
          </div>
          <div class="inv-field">
            <label class="inv-label">STATUS</label>
            <select v-model="localForm.status" class="inv-select">
              <option v-for="o in statusOptions.filter(v => v.value !== null && v.value !== 4)" :key="o.value" :value="o.value">{{ o.text }}</option>
            </select>
          </div>
          <div class="inv-field inv-field--wide">
            <label class="inv-label">ATTACH TO</label>
            <div class="inv-select">
              <v-autocomplete v-model="localForm.user_id" :items="computedUser"
              auto-select-first
              clearable
              ></v-autocomplete>
              <span v-if="localForm.user_id">Email: {{(userCollection.filter(v => v.id === localForm.user_id)[0] || {}).email }}</span>
            </div>
          </div>
          <div class="inv-field">
            <label for="datepicker-purchase_date" class="inv-label">PURCHASE DATE</label>
            <b-form-datepicker
              id="datepicker-purchase_date"
              v-model="localForm.purchase_date"
              :min="minDate"
              calendar-width="100%"
              locale="en"
              start-weekday="1"
              class="inv-input"
              style="max-width: 260px"
            ></b-form-datepicker>
          </div>
          <div class="inv-field">
            <label for="datepicker-expiration_date" class="inv-label">WARRANTY EXPIRES AT</label>
            <b-form-datepicker
              id="datepicker-expiration_date"
              v-model="localForm.warranty_expires_at"
              :min="minDate"
              calendar-width="100%"
              locale="en"
              start-weekday="1"
              class="inv-input"
              style="max-width: 260px"
            ></b-form-datepicker>
          </div>
          <div class="inv-field">
            <label for="datepicker-touch_protection_date" class="inv-label">TOUCH PROTECTION EXP. DATE</label>
            <b-form-datepicker
              id="datepicker-touch_protection_date"
              v-model="localForm.touch_protection_date"
              :min="minDate"
              calendar-width="100%"
              locale="en"
              start-weekday="1"
              class="inv-input"
              style="max-width: 260px"
            ></b-form-datepicker>
          </div>
          <div class="inv-field inv-field--full">
            <ToolsFileUpload
              @files-selected="files => localForm.attachments = [...localForm.attachments || [], ...files]"
            ></ToolsFileUpload>
          </div>
          <div class="inv-field inv-field--full">
            <label class="inv-label">NOTES</label>
            <textarea v-model="localForm.notes" class="inv-textarea" rows="3" placeholder="Optional notes..."></textarea>
          </div>
        </div>
      </div>
      <div class="inv-modal__footer">
        <button class="inv-btn inv-btn--ghost" @click="$emit('input', false)">Cancel</button>
        <button class="inv-btn inv-btn--primary" @click="submit"
        :disabled="isUploading">
          {{ editMode ? 'SAVE' : 'CREATE' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ToolsFileUpload from './ToolsFileUpload.vue'
import { mapGetters, mapActions } from "vuex";

export default {
  name: 'ToolsFormDialog',
  components: { ToolsFileUpload },
  props: {
    value:        { type: Boolean, default: false },
    editMode:     { type: Boolean, default: false },
    form:         { type: Object,  required: true },
    typeOptions:  { type: Array,   required: true },
    statusOptions:{ type: Array,   required: true },
    takenUserIds: { type: Array,default: () => [] },
  },
  data() {
    return {
      localForm: { ...this.form },
      errors: {},
      isUploading: false,
      users: [],
    }
  },
  watch: {
    form(val) { this.localForm = { ...val }; this.errors = {} },
    value(val) { if (val) { this.localForm = { ...this.form }; this.errors = {} } },
  },
  computed: {
    ...mapGetters([
      "userCollection",
    ]),
    minDate() {
      const d = new Date()
      d.setFullYear(d.getFullYear() - 5)
      return d.toISOString().slice(0, 10)
    },

    computedUser() {
      const taken = new Set((this.takenUserIds || []).map(Number));
      const currentUserId = Number(this.form.user_id || 0);

      return (this.userCollection || [])
        .filter(u => u.type === 'employee')
        .filter(u => {
          const uid = Number(u.id);
          return !taken.has(uid) || uid === currentUserId;
        })
        .map(u => ({
          text: [u.surname, u.christian_name1, u.christian_name2].filter(Boolean).join(' '),
          value: u.id,
        }));
    }
  },
  methods: {
    ...mapActions([
      "fetchUser",
    ]),
    submit() {
      this.errors = {}
      if (!this.localForm.name) { this.errors.name = true; return }
      this.$emit('submit', { ...this.localForm })
    },
  },
  mounted() {
    this.fetchUser();
    this.users = this.userCollection;
  },
}
</script>

<style scoped>
.inv-overlay {
  position: fixed; inset: 0; z-index: 999;
  background: rgba(0,0,0,0.35);
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(2px);
}
.inv-modal {
  background: var(--bg2); border: 1px solid var(--border2);
  box-shadow: 0 24px 64px rgba(0,0,0,0.15);
  display: flex; flex-direction: column; max-height: 90vh; overflow: hidden;
}
.inv-modal--lg { width: 680px; }
.inv-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid var(--border);
  font-family: 'IBM Plex Mono', monospace; font-size: 16px; font-weight: 700;
  letter-spacing: 0.08em; color: var(--text-bright); background: var(--bg3);
}
.inv-modal__close { background: none; border: none; color: var(--text-dim); font-size: 23px; cursor: pointer; line-height: 1; padding: 0 4px; }
.inv-modal__close:hover { color: var(--text); }
.inv-modal__body  { padding: 24px 20px; overflow-y: auto; flex: 1; }
.inv-modal__footer { padding: 14px 20px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 10px; background: var(--bg3); }

.inv-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.inv-field { display: flex; flex-direction: column; gap: 5px; }
.inv-field--wide { grid-column: span 2; }
.inv-field--full { grid-column: span 2; }
.inv-label { font-family: 'IBM Plex Mono', monospace; font-size: 12px; font-weight: 700; letter-spacing: 0.14em; color: var(--text-dim); }
.inv-input, .inv-select, .inv-textarea {
  background: var(--bg3); border: 1px solid var(--border);
  color: var(--text); font-family: 'IBM Plex Sans', sans-serif; font-size: 16px;
  padding: 9px 11px; outline: none; transition: border-color 0.15s;
  width: 100%; box-sizing: border-box;
}
.inv-input:focus, .inv-select:focus, .inv-textarea:focus { border-color: var(--accent); }
.inv-input--mono  { font-family: 'IBM Plex Mono', monospace; font-size: 15px; }
.inv-input--error { border-color: var(--danger) !important; }
.inv-select { appearance: none; cursor: pointer; }
.inv-textarea { resize: vertical; }
.inv-field-error { font-family: 'IBM Plex Mono', monospace; font-size: 13px; color: var(--danger); }

.inv-btn { font-family: 'IBM Plex Mono', monospace; font-size: 14px; font-weight: 600; letter-spacing: 0.1em; padding: 8px 18px; border: 1px solid; cursor: pointer; transition: all 0.15s; display: inline-flex; align-items: center; gap: 6px; }
.inv-btn--primary { background: var(--accent); border-color: var(--accent); color: #fff; }
.inv-btn--primary:hover { background: var(--accent2); border-color: var(--accent2); }
.inv-btn--ghost   { background: transparent; border-color: var(--border2); color: var(--text); }
.inv-btn--ghost:hover { border-color: var(--text-dim); }

/* ── RESPONSIVE ── */
@media (max-width: 720px) {
  .inv-modal--lg {
    width: 100%;
    max-width: 100%;
    max-height: 100vh;
    margin: 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
    align-self: flex-end;
  }
  .inv-form-grid { grid-template-columns: 1fr; }
  .inv-field--wide, .inv-field--full { grid-column: span 1; }
  .inv-modal__body { padding: 16px 14px; }
  .inv-modal__header { font-size: 14px; padding: 14px 16px; }
  .inv-modal__footer { padding: 12px 14px; }
  .inv-label { font-size: 11px; }
  .inv-input, .inv-select, .inv-textarea { font-size: 15px; }
}

@media (max-width: 480px) {
  .inv-modal__header { font-size: 13px; }
  .inv-btn { font-size: 13px; padding: 8px 14px; }
}
</style>